/**
 * Engagement-Specific Audit Configuration
 * 
 * Maps engagement audit data to UI-friendly display
 */

import { AuditFieldConfig } from './audit-config'
import type { FieldMetadata, LookupValue } from '@/types/api/engagement'

/**
 * Lookup functions passed from React component context
 * These provide ID → Name mappings for various entities
 */
export interface EngagementAuditLookups {
  getOrgName: (id: number) => string
  getProfileName: (id: number) => string
  getFieldMetadata: (propertyPath: string, fieldName: string) => FieldMetadata | undefined
  formatCurrency: (value: number) => string
  formatDate: (date: Date | string) => string
  formatTimestamp: (timestamp: Date | string) => string
  formatDecimal: (value: number, decimals: number) => string
  formatStatus: (statusCode: string) => string
  formatAction: (actionCode: string) => string
  getValue: (categoryName: string, code: string) => LookupValue | undefined
}

/**
 * Factory function to create engagement audit config
 * Must be called from React component with lookup hooks
 * 
 * @param lookups - Lookup functions for IDs → names
 * @returns Complete audit field config for engagements
 */
export function createEngagementAuditConfig(lookups: EngagementAuditLookups): AuditFieldConfig {
  return {
    // Available sections with path matching rules (ordered, exceptions first)
    availableSections: [
      {
        value: "Core Information",
        label: "Core Information",
        pathPatterns: [
          "CoreInfo/*",  // All CoreInfo except exceptions below
          "ClientInfo/*"
        ]
      },
      {
        value: "Billing setup",
        label: "Billing setup",
        pathPatterns: [
          // Exceptions: specific CoreInfo fields that belong here
          "CoreInfo/BillingTypeCode",
          "CoreInfo/TotalAmount",
          "CoreInfo/TotalInternalBudget",
          "CoreInfo/CurrencyCode",
          "CoreInfo/SalesTaxSubdivisionCode",
          "CoreInfo/ChargeSalesTax",
          // Regular billing patterns
          "Milestones/*",
          "RevenueRecognition/*"
        ]
      },
      {
        value: "Worker setup",
        label: "Worker setup",
        pathPatterns: [
          "Workers/*",
          "RemovedWorkers/*"
        ]
      },
      {
        value: "Timesheet and Expense setup",
        label: "Timesheet and Expense setup",
        pathPatterns: [
          "TimesheetInfo/*",
          "ExpenseInfo/*"
        ]
      },
      {
        value: "Tasks",
        label: "Tasks",
        pathPatterns: ["Projects/*"]
      },
      {
        value: "Invoice setup",
        label: "Invoice setup",
        pathPatterns: ["InvoiceInfo/*"]
      }
    ],

    // Field label mappings (PascalCase → Display Label)
    // TODO: Complete this mapping based on actual form labels
    fieldLabels: {
      // Core fields
      Name: "Engagement Name",
      Code: "Engagement Code",
      EngagementTypeCode: "Engagement Type",
      RequestTypeCode: "Request Type",
      StartDate: "Start Date",
      EndDate: "End Date",
      AgreementSignedDate: "Agreement Signed Date",
      ClosedDate: "Closed Date",
      ClosedReasonCode: "Closed Reason",
      InternalCompanyOrganizationId: "Internal Company",
      BranchCode: "Branch",
      SalesPersonUserProfileId: "Sales Person",
      EngagementLeadUserProfileId: "Engagement Lead",
      ProjectLeadUserProfileId: "Project Lead",
      ClientExecutiveUserProfileId: "Client Executive",
      EngagementSourceId: "Original Engagement",
      IsPreEngagement: "Pre-engagement",

      // Client fields
      ClientOrganizationId: "Client",
      ClientContactUserProfileId: "Client Contact",
      
      // Billing fields
      BillingTypeCode: "Billing Type",
      TotalAmount: "Total Amount",
      TotalInternalBudget: "Total Internal Budget",
      CurrencyCode: "Currency",
      SalesTaxSubdivisionCode: "Sales Tax",
      ChargeSalesTax: "Charge Sales Tax",
      
      // Worker fields
      WorkerUserProfileId: "Worker",
      FirstDayOfWork: "First Day of Work",
      LastDayOfWork: "Last Day of Work",
      TerminationTypeId: "Termination Type",
      TerminationReasonIds: "Termination Reasons",
      Comments: "Termination Comments",
      JobCategoryCode: "Job Category",
      JobFunctionCode: "Job Function",
      JobTitle: "Job Title",
      BudgetedHoursPerDay: "Budgeted Hours",
      BillRate: "Bill Rate",
      BillRateUnitCode: "Bill Rate Unit",
      ReportingBillRate: "Reporting Bill Rate",
      PaymentCurrencyCode: "Payment Currency",
      LineOfBusinessCode: "Line of Business",
      ScheduledPayRate: "Scheduled Pay Rate",
      ScheduledPayRateEffectiveDate: "Scheduled Pay Rate Effective Date",
      ScheduledReportingPayRate: "Scheduled Reporting Pay Rate",
      ScheduledReportingPayRateEffectiveDate: "Scheduled Reporting Pay Rate Effective Date",
      OriginalStartDate: "Original Start Date",
      ExtensionStartDate: "Extension Start Date",
      ExtensionEndDate: "Extension End Date",
      
      // Worker pay fields (nested objects)
      "Workers/TimeBasedPay/PayRate": "Pay Rate",
      "Workers/TimeBasedPay/PayRateUnitCode": "Pay Rate Unit",
      "Workers/TimeBasedPay/PayScheduleCode": "Pay Schedule",
      "Workers/T4SalariedPay/ReportingPayRate": "Reporting Pay Rate",
      "Workers/T4SalariedPay/ScheduledReportingPayRate": "Scheduled Reporting Pay Rate",
      "Workers/T4SalariedPay/ScheduledReportingPayRateEffectiveDate": "Scheduled Reporting Pay Rate Effective Date",
      // Same nested pay fields on RemovedWorker — labels duplicated because the audit
      // normalization keys off the full parent path, not just the leaf field name.
      "RemovedWorkers/TimeBasedPay/PayRate": "Pay Rate",
      "RemovedWorkers/TimeBasedPay/PayRateUnitCode": "Pay Rate Unit",
      "RemovedWorkers/TimeBasedPay/PayScheduleCode": "Pay Schedule",
      "RemovedWorkers/T4SalariedPay/ReportingPayRate": "Reporting Pay Rate",
      "RemovedWorkers/T4SalariedPay/ScheduledReportingPayRate": "Scheduled Reporting Pay Rate",
      "RemovedWorkers/T4SalariedPay/ScheduledReportingPayRateEffectiveDate": "Scheduled Reporting Pay Rate Effective Date",

      // Each rate a worker is paid at here. Rate Name is the one a reader recognises, since it is what the
      // worker sees on a timesheet; Rate Type is the slot the engagement allocated, which means nothing
      // outside back office and appears only when a rate is added.
      "Workers/PayRates/Description": "Rate Name",
      "Workers/PayRates/PayRate": "Pay Rate",
      "Workers/PayRates/PayRateUnitCode": "Pay Rate Unit",
      "Workers/PayRates/RateTypeCode": "Rate Type",
      "Workers/PayRates/ProfileRateId": "Profile Rate",
      "Workers/PayRates/ScheduledPayRate": "Scheduled Pay Rate",
      "Workers/PayRates/ScheduledPayRateEffectiveDate": "Scheduled Pay Rate Effective Date",
      "Workers/PayRates/Billable": "Billable",
      "RemovedWorkers/PayRates/Description": "Rate Name",
      "RemovedWorkers/PayRates/PayRate": "Pay Rate",
      "RemovedWorkers/PayRates/PayRateUnitCode": "Pay Rate Unit",
      "RemovedWorkers/PayRates/RateTypeCode": "Rate Type",
      "RemovedWorkers/PayRates/ProfileRateId": "Profile Rate",
      "RemovedWorkers/PayRates/ScheduledPayRate": "Scheduled Pay Rate",
      "RemovedWorkers/PayRates/ScheduledPayRateEffectiveDate": "Scheduled Pay Rate Effective Date",
      "RemovedWorkers/PayRates/Billable": "Billable",

      // Scheduled changes to budgeted hours, labelled as the Schedule Change dialog labels them. The
      // stamps record the nightly run applying a change, or a save cancelling one.
      "Workers/ScheduledBudgetedHours/EffectiveDate": "Effective Date",
      "Workers/ScheduledBudgetedHours/HoursPerDay": "Hours per Day",
      "Workers/ScheduledBudgetedHours/AppliedDate": "Applied On",
      "Workers/ScheduledBudgetedHours/CancelledChange": "Cancelled Change",
      "Workers/ScheduledBudgetedHours/CancelledDate": "Cancelled On",
      "Workers/ScheduledBudgetedHours/PreviousHoursPerDay": "Previous Hours per Day",
      "RemovedWorkers/ScheduledBudgetedHours/EffectiveDate": "Effective Date",
      "RemovedWorkers/ScheduledBudgetedHours/HoursPerDay": "Hours per Day",
      "RemovedWorkers/ScheduledBudgetedHours/AppliedDate": "Applied On",
      "RemovedWorkers/ScheduledBudgetedHours/CancelledChange": "Cancelled Change",
      "RemovedWorkers/ScheduledBudgetedHours/CancelledDate": "Cancelled On",
      "RemovedWorkers/ScheduledBudgetedHours/PreviousHoursPerDay": "Previous Hours per Day",

      // Timesheet fields
      TimesheetCycleCode: "Timesheet Cycle",
      TimesheetApprovalFlowCode: "Approval Sequence",
      "TimesheetInfo/Approvers/UserProfileId": "Approver",
      
      // Expense fields
      ExpenseMethodologyCode: "Expense Methodology",
      "ExpenseInfo/Approvers/UserProfileId": "Approver",
      
      // Invoice fields
      AlternateBillCode: "Alternate Bill",
      BillingFrequencyCode: "Billing Frequency",
      PaymentTermsCode: "Payment Terms",
      InvoiceTemplateCode: "Invoice Template",
      InvoiceRecipientUserProfileId: "Invoice Recipient",
      InvoiceDeliveryMethodCode: "Delivery Method",
      IncludeTimesheet: "Include Timesheet",
      InvoiceCcEmails: "CC Emails",
      
      // Milestone fields
      MilestoneReference: "Reference",
      Description: "Description",
      StatusCode: "Status",
      Amount: "Amount",
      InternalBudget: "Internal Budget",
      
      // Revenue Recognition fields
      "RevenueRecognition/Periods/IsLocked": "Locked",
      
      // Task fields
      TaskName: "Task Name",
      Billable: "Billable",
      Inactive: "Inactive",
      IsUtilized: "Is Utilized",
    },

    // Path segment formatter - formats individual segments of propertyPath
    // Explicitly excludes organizational containers that are already represented by sections
    formatPathSegment: (segment: string, isIndex: boolean) => {
      if (isIndex) {
        // Format numeric indices as "#1", "#2", etc.
        return `#${parseInt(segment) + 1}`
      }
      
      // Explicitly exclude organizational containers (already represented by sections)
      // Also exclude nested pay type objects so fields appear directly under Worker
      const excludeFromParentLabel = [
        'CoreInfo',
        'ClientInfo',
        'InvoiceInfo',
        'TimeBasedPay',
        'T4SalariedPay'
      ]
      if (excludeFromParentLabel.includes(segment)) {
        return undefined
      }
      
      // Format collection segments and section containers
      const collectionMap: Record<string, string> = {
        Milestones: "Milestone",
        Periods: "Period",
        Workers: "Worker",
        RemovedWorkers: "Removed Worker",
        PayRates: "Pay Rate",
        ScheduledBudgetedHours: "Scheduled Hours Change",
        Projects: "Task",
        RevenueRecognition: "Revenue Recognition",
        TimesheetInfo: "Timesheet",
        ExpenseInfo: "Expense",
      }
      
      // Return formatted label or the segment as-is (default behavior)
      return collectionMap[segment] || segment
    },

    // Value formatter - uses metadata-driven formatting
    formatValue: function formatValue(value: any, propertyPath: string, fieldName: string): string {
      // Handle null/undefined
      if (value === null || value === undefined) {
        return "—"
      }

      // Arrays: format each element via the same path and join.
      // Audit framework can emit array-valued fields (e.g. multi-select lookup IDs)
      // as a single entry with the whole array as the value.
      if (Array.isArray(value)) {
        const formatted = value.map((v) => formatValue(v, propertyPath, fieldName))
        return formatted.length === 0 ? "—" : formatted.join(", ")
      }

      // Get field metadata
      const metadata = lookups.getFieldMetadata(propertyPath, fieldName)

      // Use metadata format if available
      if (metadata?.format) {
        switch (metadata.format) {
          case 'lookup': {
            if (metadata.category) {
              const lookupValue = lookups.getValue(metadata.category, String(value))
              return lookupValue?.value || String(value)
            }
            break
          }
          case 'organization':
            return lookups.getOrgName(value as number)
          case 'profile':
            return lookups.getProfileName(value as number)
          case 'date':
            return lookups.formatDate(value)
          case 'date-time':
            return lookups.formatTimestamp(value)
          case 'double':
            return lookups.formatDecimal(value as number, 2)
        }
      }

      // Fallback to type-based formatting
      if (typeof value === "boolean") {
        return value ? "Yes" : "No"
      }

      if (typeof value === "string") {
        // Auto-detect dates by format
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
          return lookups.formatTimestamp(value)
        }
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return lookups.formatDate(value)
        }
      }

      if (typeof value === "number") {
        // Format numbers based on metadata type
        if (metadata?.type === 'number' && metadata?.format === 'double') {
          return lookups.formatDecimal(value, 2)
        }
        // For integers or unmapped numbers, return as-is
        return String(value)
      }

      return String(value)
    },

    // Status formatter - just return as-is since engagement API returns readable strings
    // Values like "Draft", "PendingReview" should be formatted by caller if needed
    formatStatus: lookups.formatStatus,

    // Action formatter - just return as-is
    formatAction: lookups.formatAction,

    // The id a scheduled hours change is matched on between saves; the change is read by its date.
    hiddenFields: [
      "Workers/ScheduledBudgetedHours/Id",
      "RemovedWorkers/ScheduledBudgetedHours/Id",
    ],
  }
}
